/** @format */

import React, { useState, useRef, useMemo, useContext } from 'react'
import styled from 'styled-components'
import {
  _$,
  Drawer,
  Form,
  FormItem,
  Icon,
  Input,
  Spinner,
  Message,
  Radio,
  Select,
  Option,
  Tip,
  Tooltip,
  Tag,
  Checkbox,
  Button
} from '@king-design/react'
import { AddButon, AlarmCallBackNew, AlarmReciverNew } from 'src/components'
import {
  TriggerRecordItemProp,
  TriggerDetailItemProp,
  ActionTypeEnum,
  NoticeTypeEnum,
  TriggerTypeEnum,
  initTriggerCondition,
  initTriggerConditionAgg,
  getCronSpecValue,
  getOutputParams
} from '../../container'
import TriggerCondition from './TriggerCondition'
import TriggerCodeMirror from './TriggerCodeMirror'
import EventHistory from './EventHistory'
import { getTriggerDetail, saveTrigger, approveTrigger } from 'src/api/ApiEventOpsTrigger'
import { getFlowParamsInfo } from 'src/pages/EventOps/Scene/container'
import { cloneDeep, isNumber } from 'lodash'
import { useAsync, useAsyncFn } from 'react-use'
import variable from 'src/static/styles/var'
import Readonly from './Readonly'
import { CountContext } from '../../index'
import { getScene } from 'src/api/ApiEventOpsScene'
import { handleWayToChannels } from 'src/components/AlarmReciver/new'

interface RuleDrawerProps {
  className?: string
  visible: boolean
  onClose: (update?: boolean) => void
  editable: boolean
  record?: TriggerRecordItemProp
  openMethod?: 'sign' | 'ID'
  onEdit: (editable: boolean) => void
}

const initFormValues: TriggerDetailItemProp = {
  ID: null,
  Sign: '',
  Name: '',
  TriggerType: 'monitor_event',
  TriggerConditions: {
    Conditions: [[initTriggerCondition]],
    CronSpec: '0 0 * * * *'
  },
  ActionType: 'location',
  ActionSign: '',
  ActionParams: {
    Outputs: [],
    ScriptID: null
  },
  Notice: {
    Events: ['trigger_before', 'trigger_after', 'trigger_after_fail'],
    UrlNotices: [],
    UserNotices: []
  },
  Limiter: {
    Max: 0,
    Duration: 0
  },
  Approving: false
}

const initVersion = {
  versionA: 0,
  versionB: 0,
  versionC: 1,
  version_remark: ''
}
const RuleDrawer: React.FC<RuleDrawerProps> = ({
  className,
  visible,
  onClose,
  editable,
  record,
  onEdit,
  openMethod = 'sign'
}) => {
  const { sceneList, fetchSceneList, reciverResult } = useContext(CountContext)
  // form表单
  const formRef = useRef(null)
  const [formValues, setFormValues] = useState<TriggerDetailItemProp>(initFormValues)
  // 查看历史事件
  const [eventHistoryVisible, setEventHistoryVisible] = useState(false)
  // 自定义场景输入参数
  const [sceneInput, setSceneInput] = useState<string[]>([])
  // 当前暂存的ID（发布时候使用）
  const [saveInfo, setSaveInfo] = useState<{ id: number; sign: string }>()
  // 版本信息弹窗
  const versionFormRef = useRef(null)
  const [versionVisible, setVersionVisible] = useState(false)
  const [version, setVersion] = useState(initVersion)

  // 获取场景
  const [, fetchScene] = useAsyncFn(async ID => {
    const result = await getScene(ID)
    return result
  }, [])

  const { value: triggerDetail } = useAsync(async () => {
    if (visible) {
      if (record) {
        const result = await getTriggerDetail({
          ID: openMethod === 'ID' ? record?.ID : record?.Sign,
          Latest: editable ? true : false
        })
        const {
          ID,
          Sign,
          Name,
          TriggerType,
          TriggerConditions,
          ActionType,
          ActionSign,
          ActionParams,
          Notice,
          Limiter,
          Approving
        } = result
        if (ActionType === 'location') {
          const sceneResult = await fetchScene(ActionSign)
          if (sceneResult) {
            const inputParams = await getFlowParamsInfo(sceneResult, 'input')
            setSceneInput(inputParams)
          }
        }
        setFormValues({
          ID,
          Sign,
          Name,
          TriggerType,
          TriggerConditions: {
            ...initFormValues.TriggerConditions,
            ...TriggerConditions
          },
          ActionType,
          ActionSign,
          ActionParams,
          Notice: {
            ...Notice,
            UserNotices: Notice.UserNotices.map(item => ({
              ...item,
              ContactChannels: handleWayToChannels(item.ContactChannels || item.ContactWay)
            }))
          },
          Limiter,
          Approving
        })
        return {
          ...result,
          Notice: {
            ...Notice,
            UserNotices: Notice.UserNotices.map(item => ({
              ...item,
              ContactChannels: handleWayToChannels(item.ContactChannels || item.ContactWay)
            }))
          }
        }
      } else {
        return {}
      }
    } else {
      setFormValues({
        ...initFormValues
      })
      setSceneInput([])
    }
  }, [visible, record?.ID, editable])

  const onChangeValues = values => {
    setFormValues({
      ...formValues,
      ...values
    })
  }

  // 规则title渲染逻辑
  const renderDrawerTitle = () => {
    if (!record?.ID) return _$('新增规则') // 新增规则：无sign值，为新增规则
    if (editable) {
      // 编辑规则：只展示title
      return <span>{record?.Name}</span>
    } else {
      return (
        <div>
          <span>{record?.Name}</span>
          {record.Status !== -2 && !record.Approving && (
            <a
              style={{ marginLeft: 16 }}
              onClick={() => {
                onEdit(true)
              }}
            >
              <Icon color="#0091EA" size="14" className="ion-edit"></Icon>
              <span style={{ marginLeft: 4 }}>{_$('编辑')}</span>
            </a>
          )}
        </div>
      )
    }
  }

  // 发布
  const onPublish = async () => {
    const saveResult = await onSave()
    setSaveInfo({
      id: saveResult?.ID,
      sign: saveResult?.Sign
    })
    if (saveResult) {
      setVersionVisible(true)
    }
  }

  // 暂存
  const onSave = async (close?: boolean) => {
    const validate = await formRef?.current?.validate()
    if (validate) {
      const result = await saveTrigger({
        ...formValues,
        ID: formValues?.ID || saveInfo?.id || null,
        Sign: formValues?.Sign || saveInfo?.sign || null
      })
      if (result) {
        Message.success(formValues?.ID ? _$('保存成功') : _$('创建成功'))
        close && onClose(true)
      }
      return result
    }
  }

  // 定时触发:解析crontab表达式
  const cronSpecValue = useMemo(() => {
    return getCronSpecValue(formValues.TriggerConditions?.CronSpec)
  }, [formValues.TriggerConditions?.CronSpec])

  // 是否可以查看历史事件
  const isEventHistory = isNumber(formValues.TriggerConditions?.Conditions?.[0]?.[0]?.Value)

  return (
    <div className={className} id="rule-drawer">
      <Drawer
        value={visible}
        width={800}
        slotHeader={renderDrawerTitle()}
        container={() => document.getElementById('rule-drawer')}
        mode="destroy"
        slotFooter={
          editable ? (
            <div>
              <Button
                onClick={() => {
                  onSave(true)
                }}
              >
                {_$('暂存')}
              </Button>
              <Tooltip content={_$('正式发布前会先走审批流程进行审批，审批通过后自动发布。')}>
                <Button
                  type="primary"
                  onClick={() => {
                    onPublish()
                  }}
                >
                  {_$('发布')}
                </Button>
              </Tooltip>
            </div>
          ) : (
            <Button
              onClick={() => {
                onClose()
              }}
            >
              {_$('关闭')}
            </Button>
          )
        }
        onChangeValue={show => {
          if (!show) {
            onClose()
          }
        }}
      >
        <>
          {record?.Approving && !editable ? (
            <Tip type="warning" showIcon>
              <div>
                {_$('存在审批中的版本，暂不允许进行编辑操作。如确实需要，请先撤回审批。')}
                <a
                  type="link"
                  onClick={() => {
                    triggerDetail?.ApprovingID &&
                      window.open(
                        `/#!/monitor/eventops/approve/trigger/${triggerDetail?.ApprovingID}`,
                        '_blank'
                      )
                  }}
                >
                  {_$('审批详情')}
                </a>
              </div>
            </Tip>
          ) : null}
          <div>
            {editable ? (
              <Form starOnRequired ref={formRef} labelWidth={100} className="form_content">
                <FormItem label={_$('名称')} value={formValues.Name} rules={{ required: true }}>
                  <Input
                    width={650}
                    value={formValues.Name}
                    onChangeValue={value => {
                      setFormValues({ ...formValues, Name: value })
                    }}
                  />
                </FormItem>
                <FormItem
                  label={_$('触发源')}
                  value={formValues.TriggerType}
                  rules={{ required: true }}
                >
                  {Object.entries(TriggerTypeEnum).map(([value, label]) => (
                    <Radio
                      key={value}
                      name="TriggerType"
                      trueValue={value}
                      value={formValues.TriggerType}
                      onChangeValue={value =>
                        setFormValues({
                          ...formValues,
                          TriggerConditions: {
                            Conditions:
                              value === 'monitor_event_agg'
                                ? [[initTriggerConditionAgg]]
                                : [[initTriggerCondition]],
                            CronSpec: '0 0 * * * *'
                          },
                          TriggerType: value as keyof typeof TriggerTypeEnum
                        })
                      }
                    >
                      {_$(label)}
                    </Radio>
                  ))}
                </FormItem>
                {/* 触发源：事件聚合 || 事件 */}
                {formValues.TriggerType === 'monitor_event' ||
                formValues.TriggerType === 'monitor_event_agg' ? (
                  <FormItem
                    label={_$('触发规则')}
                    value={formValues.TriggerConditions?.Conditions}
                    rules={{
                      required: true,
                      minLength: 1
                    }}
                  >
                    {formValues.TriggerConditions?.Conditions?.map((condition, orIndex) => (
                      <TriggerCondition
                        triggerType={formValues?.TriggerType}
                        key={orIndex}
                        orIndex={orIndex}
                        conditions={condition}
                        onChange={(conditions, orIndex, move) => {
                          const copyTriggerConditions = cloneDeep(
                            formValues.TriggerConditions?.Conditions
                          )
                          if (!conditions) {
                            // 删除
                            copyTriggerConditions.splice(orIndex, 1)
                          } else if (move === 'up') {
                            // 上移
                            if (orIndex > 0) {
                              const temp = copyTriggerConditions[orIndex - 1]
                              copyTriggerConditions[orIndex - 1] = copyTriggerConditions[orIndex]
                              copyTriggerConditions[orIndex] = temp
                            }
                          } else if (move === 'down') {
                            // 下移
                            if (orIndex < copyTriggerConditions.length - 1) {
                              const temp = copyTriggerConditions[orIndex + 1]
                              copyTriggerConditions[orIndex + 1] = copyTriggerConditions[orIndex]
                              copyTriggerConditions[orIndex] = temp
                            }
                          } else {
                            copyTriggerConditions[orIndex] = conditions
                          }
                          setFormValues({
                            ...formValues,
                            TriggerConditions: {
                              ...formValues.TriggerConditions,
                              Conditions: copyTriggerConditions
                            }
                          })
                        }}
                      />
                    ))}
                    <AddButon
                      text={_$('或（Or）')}
                      onClick={() => {
                        onChangeValues({
                          TriggerConditions: {
                            Conditions: formValues.TriggerConditions?.Conditions?.concat([
                              [initTriggerCondition]
                            ])
                          }
                        })
                      }}
                    />
                  </FormItem>
                ) : null}
                {formValues.TriggerType === 'monitor_event' ? (
                  <div className="event_history">
                    <a
                      className={isEventHistory ? '' : 'disabled-text'}
                      onClick={() => {
                        if (isEventHistory) {
                          setEventHistoryVisible(true)
                        }
                      }}
                    >
                      {_$('查看历史事件')}
                    </a>
                    <EventHistory
                      visible={eventHistoryVisible}
                      onClose={() => setEventHistoryVisible(false)}
                      conditions={formValues.TriggerConditions?.Conditions}
                    />
                  </div>
                ) : null}
                {/* 触发源：定时任务 */}
                {formValues.TriggerType === 'crontab' ? (
                  <FormItem
                    className="form_item_crontab"
                    slotLabel={
                      <>
                        {_$('定时任务')}
                        <Tooltip
                          content={_$('示例：[0 8,15 * * 1-5]每周一到周五的8:00和15:00执行一次。')}
                        >
                          <Icon className="icon ion-help-circled" />
                        </Tooltip>
                      </>
                    }
                    value={formValues.TriggerConditions?.CronSpec}
                    rules={{ required: true }}
                  >
                    <div className="ksc-space">
                      <FormItem
                        hideLabel
                        value={cronSpecValue?.minute}
                        rules={{
                          required: true,
                          unique: (value: string) => {
                            return /^(\d+|\*)([,-/](\d+|\*))*$/.test(value) || _$('格式不正确')
                          }
                        }}
                      >
                        <Input
                          width={120}
                          value={cronSpecValue?.minute}
                          slotPrepend={_$('分')}
                          onChangeValue={value =>
                            setFormValues({
                              ...formValues,
                              TriggerConditions: {
                                ...formValues.TriggerConditions,
                                CronSpec: `0 ${value} ${cronSpecValue?.hour} ${cronSpecValue?.dom} ${cronSpecValue?.month} ${cronSpecValue?.dow}`
                              }
                            })
                          }
                        />
                      </FormItem>
                      <FormItem
                        hideLabel
                        value={cronSpecValue?.hour}
                        rules={{
                          required: true,
                          unique: (value: string) => {
                            return /^(\d+|\*)([,-/](\d+|\*))*$/.test(value) || _$('格式不正确')
                          }
                        }}
                      >
                        <Input
                          width={120}
                          value={cronSpecValue?.hour}
                          slotPrepend={_$('时')}
                          onChangeValue={value =>
                            setFormValues({
                              ...formValues,
                              TriggerConditions: {
                                ...formValues.TriggerConditions,
                                CronSpec: `0 ${cronSpecValue?.minute} ${value} ${cronSpecValue?.dom} ${cronSpecValue?.month} ${cronSpecValue?.dow}`
                              }
                            })
                          }
                        />
                      </FormItem>
                      <FormItem
                        hideLabel
                        value={cronSpecValue?.dom}
                        rules={{
                          required: true,
                          unique: (value: string) => {
                            return /^(\d+|\*|\?)([,-/?](\d+|\*))*$/.test(value) || _$('格式不正确')
                          }
                        }}
                      >
                        <Input
                          width={120}
                          value={cronSpecValue?.dom}
                          slotPrepend={_$('日')}
                          onChangeValue={value =>
                            setFormValues({
                              ...formValues,
                              TriggerConditions: {
                                ...formValues.TriggerConditions,
                                CronSpec: `0 ${cronSpecValue?.minute} ${cronSpecValue?.hour} ${value} ${cronSpecValue?.month} ${cronSpecValue?.dow}`
                              }
                            })
                          }
                        />
                      </FormItem>
                      <FormItem
                        hideLabel
                        value={cronSpecValue?.month}
                        rules={{
                          required: true,
                          unique: (value: string) => {
                            return /^(\d+|\*)([,-/](\d+|\*))*$/.test(value) || _$('格式不正确')
                          }
                        }}
                      >
                        <Input
                          width={120}
                          value={cronSpecValue?.month}
                          slotPrepend={_$('月')}
                          onChangeValue={value =>
                            setFormValues({
                              ...formValues,
                              TriggerConditions: {
                                ...formValues.TriggerConditions,
                                CronSpec: `0 ${cronSpecValue?.minute} ${cronSpecValue?.hour} ${cronSpecValue?.dom} ${value} ${cronSpecValue?.dow}`
                              }
                            })
                          }
                        />
                      </FormItem>
                      <FormItem
                        hideLabel
                        value={cronSpecValue?.dow}
                        rules={{
                          required: true,
                          unique: (value: string) => {
                            return /^(\d+|\*|\?)([,-/?](\d+|\*))*$/.test(value) || _$('格式不正确')
                          }
                        }}
                      >
                        <Input
                          width={120}
                          value={cronSpecValue?.dow}
                          slotPrepend={_$('周')}
                          onChangeValue={value =>
                            setFormValues({
                              ...formValues,
                              TriggerConditions: {
                                ...formValues.TriggerConditions,
                                CronSpec: `0 ${cronSpecValue?.minute} ${cronSpecValue?.hour} ${cronSpecValue?.dom} ${cronSpecValue?.month} ${value}`
                              }
                            })
                          }
                        />
                      </FormItem>
                    </div>
                  </FormItem>
                ) : null}
                <FormItem
                  label={_$('触发动作')}
                  value={formValues.ActionType}
                  rules={{ required: true }}
                >
                  <div className="ksc-space">
                    {Object.entries(ActionTypeEnum).map(([value, label]) => (
                      <Radio
                        key={value}
                        trueValue={value}
                        name="action_type"
                        value={formValues.ActionType}
                        onChangeValue={value =>
                          setFormValues({
                            ...formValues,
                            ActionType: value as keyof typeof ActionTypeEnum,
                            Notice: {
                              ...formValues.Notice,
                              Events:
                                value === 'location'
                                  ? ['trigger_before', 'trigger_after', 'trigger_after_fail']
                                  : ['trigger_after_fail']
                            }
                          })
                        }
                      >
                        {_$(label)}
                      </Radio>
                    ))}
                  </div>
                </FormItem>
                {/* 触发动作：自动化场景 */}
                {formValues.ActionType === 'location' ? (
                  <FormItem
                    label={_$('场景名称')}
                    value={formValues.ActionSign}
                    rules={{ required: true }}
                  >
                    <div className="ksc-space">
                      <Select
                        width={600}
                        value={formValues.ActionSign}
                        clearable
                        filterable
                        onChangeValue={async value => {
                          setFormValues({
                            ...formValues,
                            ActionSign: value
                          })

                          const sceneResult = await fetchScene(value)
                          const inputParams = await getFlowParamsInfo(sceneResult, 'input')
                          setSceneInput(inputParams)
                        }}
                      >
                        {sceneList?.map(item => (
                          <Option key={item.Sign} value={item.Sign} label={item.Name}>
                            <div>
                              <span style={{ marginRight: 4 }}>{item.Name}</span>
                              {item.Status !== 2 && (
                                <Tooltip content={_$('此场景还未发布，关联后本触发器只可暂存')}>
                                  <Icon size={14} color="danger" className="k-icon-information" />
                                </Tooltip>
                              )}
                            </div>
                          </Option>
                        ))}
                      </Select>
                      <a
                        onClick={async () => {
                          fetchSceneList?.()
                          setFormValues({ ...formValues, ActionSign: '' })
                        }}
                      >
                        {_$('刷新')}
                      </a>
                    </div>
                    {formValues.ActionSign ? (
                      <div className="form_item_extra">
                        <span>{_$('触发场景所需参数：')}</span>
                        {sceneInput?.length ? (
                          <>
                            <span>
                              {sceneInput.map(item => {
                                return (
                                  <Tag
                                    key={item}
                                    border="solid"
                                    type={
                                      formValues.ActionParams?.Outputs?.includes(item)
                                        ? 'success'
                                        : 'danger'
                                    }
                                  >
                                    {item}
                                  </Tag>
                                )
                              })}
                            </span>
                            <span>{_$('。请在下方数据处理中，使用g_print函数输出所需参数。')}</span>
                          </>
                        ) : (
                          _$('无')
                        )}
                      </div>
                    ) : null}
                  </FormItem>
                ) : null}
                {/*数据处理 */}
                <FormItem value={true} rules={{ required: true }} label={_$('数据处理')}>
                  <TriggerCodeMirror
                    scriptID={formValues.ActionParams?.ScriptID}
                    triggerType={formValues.TriggerType}
                    onChange={(scriptID, content, lang) => {
                      const outputs = getOutputParams(content, lang)
                      setFormValues(pre => {
                        return {
                          ...pre,
                          ActionParams: {
                            ScriptID: scriptID,
                            Outputs: outputs || []
                          }
                        }
                      })
                    }}
                  />
                  <p className="form_item_extra">
                    {_$('请在数据转换中将原始参数转换为目标动作所需参数')}
                  </p>
                </FormItem>
                <FormItem label={_$('通知订阅')} value={formValues.Notice?.Events}>
                  {Object.entries(NoticeTypeEnum).map(([value, label]) =>
                    formValues.ActionType === 'custom' && value === 'trigger_before' ? null : (
                      <Checkbox
                        key={value}
                        name="notice"
                        trueValue={value}
                        value={formValues.Notice?.Events}
                        onChangeValue={value => {
                          setFormValues({
                            ...formValues,
                            Notice: { ...formValues.Notice, Events: value }
                          })
                        }}
                      >
                        {_$(label)}
                      </Checkbox>
                    )
                  )}
                </FormItem>
                <FormItem
                  label={_$('通知联系人')}
                  value={formValues.Notice?.UserNotices}
                  messages={{
                    required: _$('通知联系人&通知回调至少选择一项配置')
                  }}
                  rules={{
                    required:
                      formValues.Notice?.Events?.length &&
                      formValues.Notice?.UrlNotices.length === 0
                  }}
                >
                  <AlarmReciverNew
                    style={{ width: 650 }}
                    value={formValues.Notice?.UserNotices}
                    userlist={reciverResult?.userlist}
                    grouplist={reciverResult?.grouplist}
                    onChange={value => {
                      onChangeValues({ Notice: { ...formValues.Notice, UserNotices: value } })
                    }}
                  />
                </FormItem>
                <FormItem
                  label={_$('通知回调')}
                  value={formValues.Notice?.UrlNotices}
                  messages={{
                    required: _$('通知联系人&通知回调至少选择一项配置')
                  }}
                  rules={{
                    required:
                      formValues.Notice?.Events?.length &&
                      formValues.Notice?.UserNotices.length === 0
                  }}
                >
                  <AlarmCallBackNew
                    style={{ width: 650 }}
                    showCallbackType={false}
                    value={formValues?.Notice?.UrlNotices}
                    onChange={value => {
                      onChangeValues({ Notice: { ...formValues.Notice, UrlNotices: value } })
                    }}
                  />
                </FormItem>
                <FormItem
                  label={_$('熔断机制')}
                  value={formValues.Limiter}
                  rules={{ required: true }}
                >
                  <div className="form_item_space">
                    <p>{_$('滑动窗口')}</p>
                    <FormItem
                      hideLabel
                      value={formValues.Limiter.Duration}
                      rules={{
                        required: true,
                        unique: value => {
                          const max = formValues.Limiter.Max
                          if (max === 0 && value > 0) {
                            return _$('最多触发填0次时，滑动窗口只能填0')
                          }
                          return true
                        }
                      }}
                    >
                      <Spinner
                        width={110}
                        min={0}
                        forceStep
                        value={parseInt(formValues.Limiter.Duration + '')}
                        onChangeValue={value => {
                          onChangeValues({ Limiter: { ...formValues.Limiter, Duration: value } })
                        }}
                      />
                    </FormItem>
                    <p>{_$('秒内, 最多触发')}</p>
                    <FormItem
                      hideLabel
                      value={formValues.Limiter.Max}
                      rules={{
                        required: true,
                        unique: value => {
                          const duration = formValues.Limiter.Duration
                          if (duration === 0 && value > 0) {
                            return _$('滑动窗口填0时，最多触发只能填0')
                          }
                          return true
                        }
                      }}
                    >
                      <Spinner
                        width={110}
                        min={0}
                        forceStep
                        value={parseInt(formValues.Limiter.Max + '')}
                        onChangeValue={value => {
                          onChangeValues({ Limiter: { ...formValues.Limiter, Max: value } })
                        }}
                      />
                    </FormItem>
                    <p>{_$('次。')}</p>
                  </div>
                  <p className="form_item_extra">
                    {_$(
                      '滑动窗口期内本触发器触发动作超过设置次数时将开启熔断。任意值为0时，不会触发熔断机制。'
                    )}
                  </p>
                </FormItem>
              </Form>
            ) : (
              <Readonly record={triggerDetail as TriggerDetailItemProp} sceneInput={sceneInput} />
            )}
          </div>
        </>
      </Drawer>
      {/* 版本信息 */}
      <Drawer
        title={_$('版本信息')}
        width={600}
        value={versionVisible}
        container={() => document.getElementById('rule-drawer')}
        onChangeValue={show => {
          setVersionVisible(show)
          if (!show) {
            setVersion(initVersion)
          }
        }}
        ok={async () => {
          const validate = await versionFormRef?.current?.validate()
          if (validate && saveInfo.id) {
            const result = await approveTrigger({
              Module: 'trigger',
              TargetID: saveInfo.id,
              VersionNumber: version.versionA + '.' + version.versionB + '.' + version.versionC,
              VersionRemark: version.version_remark
            })
            if (result) {
              Message.success(_$('发布成功'))
              setVersionVisible(false)
              setVersion(initVersion)
              onClose(true)
              window.open(`/#!/monitor/eventops/approve/trigger/${saveInfo?.id}`, '_blank')
            }
          }
        }}
      >
        <Form starOnRequired ref={versionFormRef} labelWidth={80}>
          <FormItem
            label={_$('新版本号')}
            value={true}
            rules={{
              required: true,
              unique: () => {
                if (
                  version.versionA !== undefined &&
                  version.versionB !== undefined &&
                  version.versionC !== undefined
                ) {
                  if (triggerDetail?.CurrentVersion) {
                    const [versionA, versionB, versionC] = triggerDetail?.CurrentVersion.split('.')
                    if (
                      parseInt(versionA) >= version.versionA &&
                      parseInt(versionB) >= version.versionB &&
                      parseInt(versionC) >= version.versionC
                    ) {
                      return _$('新的版本号需要大于当前版本号')
                    } else {
                      return true
                    }
                  } else {
                    return true
                  }
                } else {
                  return _$('请填写完整版本号')
                }
              }
            }}
          >
            <div className="form_item_space">
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionA + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionA: value })
                  }}
                />
              </FormItem>
              <p>.</p>
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionB + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionB: value })
                  }}
                />
              </FormItem>
              <p>.</p>
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionC + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionC: value })
                  }}
                />
              </FormItem>
            </div>
            <p className="form_item_extra">{`当前版本为${
              triggerDetail?.CurrentVersion || _$('无')
            }`}</p>
          </FormItem>
          <FormItem
            label={_$('版本说明')}
            value={version.version_remark}
            rules={{ required: true }}
          >
            <Input
              width={450}
              type="textarea"
              rows={4}
              placeholder={_$('请填写版本说明')}
              value={version.version_remark}
              onChangeValue={value => setVersion({ ...version, version_remark: value })}
            />
            <p className="form_item_extra">{_$('本次更新的重点内容说明')}</p>
          </FormItem>
        </Form>
      </Drawer>
    </div>
  )
}

export default styled(RuleDrawer)`
  .form_content > .k-form-item > .k-form-content {
    width: 650px;
  }
  .k-warning {
    margin-bottom: 16px;
  }
  .k-form-label {
    .k-icon {
      margin-left: 4px;
    }
  }
  .form_item_crontab {
    .k-form-content {
      .k-form-item {
        margin-bottom: 0;
      }
    }
  }
  .form_item_extra {
    color: #999;
    margin: 4px 0;
    .k-tag {
      margin-right: 4px;
    }
  }
  .form_list {
    .ksc-space {
      margin-bottom: 8px;
      .ion-trash-a {
        width: 20px;
        text-align: center;
        line-height: 26px;
        cursor: pointer;
        &:hover {
          color: ${variable.primaryColor};
        }
      }
    }
  }
  .event_history {
    margin-top: 8px;
    text-align: right;
  }
  .k-dropdown-menu {
    .k-tag {
      height: auto;
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }
  .k-checkbox,
  .k-radio {
    margin-right: 8px;
  }
  .form_item_space {
    display: flex;
    p {
      line-height: 32px;
      margin-bottom: 0;
    }
    .k-form-item {
      margin-bottom: 0;
      .k-spinner {
        margin: 0 8px;
      }
      .k-form-error {
        position: static;
      }
    }
  }
`
